import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import FieldMappingsTable from "@/components/field-mappings/field-mappings-table";
import FieldMappingForm from "@/components/field-mappings/field-mapping-form";
import BulkUpdateForm from "@/components/bulk-update/bulk-update-form";
import { ViewGuard, EditGuard } from "@/components/auth/PermissionGuards";
import { PageWrapper } from "@/components/layout/PageWrapper";

interface FieldMappingsContentProps {
  onRefreshReady?: (refreshFn: () => Promise<void>, isRefreshing: boolean) => void;
}

const FieldMappingsContent = ({ onRefreshReady }: FieldMappingsContentProps) => {
  const [showAddMappingModal, setShowAddMappingModal] = useState(false);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
    await queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/all"] });
    await queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/choices"] });
    await queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/statistics"] });
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Expose refresh function to parent
  React.useEffect(() => {
    if (onRefreshReady) {
      onRefreshReady(handleRefresh, isRefreshing);
    }
  }, [onRefreshReady, isRefreshing]);

  return (
    <ViewGuard
      section="fieldMappings"
      fallback={
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8">
            <div className="mb-4">
              <span className="material-icons text-6xl text-gray-400">block</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
            <p className="text-gray-600">You don't have permission to view Field Mappings</p>
          </div>
        </div>
      }
    >
      <PageWrapper padding="small">
        <FieldMappingsTable onAddMapping={() => setShowAddMappingModal(true)} />

        <EditGuard section="fieldMappings" showFallback={false}>
          <div className="fixed bottom-8 right-8 flex flex-col gap-2">
            <Button
              className="bg-[#0078D4] hover:bg-[#2B88D8] shadow-lg"
              onClick={() => setShowBulkUpdateModal(true)}
            >
              <span className="material-icons text-sm mr-2">publish</span>
              Bulk Update
            </Button>
            <Button
              className="bg-[#0078D4] hover:bg-[#2B88D8] shadow-lg"
              onClick={() => setShowAddMappingModal(true)}
            >
              <span className="material-icons text-sm mr-2">add</span>
              Add Mapping
            </Button>
          </div>
        </EditGuard>

        {showAddMappingModal && (
          <FieldMappingForm
            isOpen={showAddMappingModal}
            onClose={() => setShowAddMappingModal(false)}
            onSuccess={() => {
              setShowAddMappingModal(false);
              handleRefresh();
            }}
          />
        )}

        {showBulkUpdateModal && (
          <BulkUpdateForm
            isOpen={showBulkUpdateModal}
            onClose={() => setShowBulkUpdateModal(false)}
            onSuccess={() => {
              setShowBulkUpdateModal(false);
              handleRefresh();
            }}
          />
        )}
      </PageWrapper>
    </ViewGuard>
  );
};

export default FieldMappingsContent;
